import { z } from 'zod';
import { IsoDateTime, SortDirection, UserRef, Uuid, Version } from './common.js';
import { DocumentIcon } from './documents.js';

/**
 * Inline databases inside documents (ADR-0055): typed properties, rows, views and relations. A database
 * lives in its home document and takes that document's access; `database` blocks show it in any
 * document without widening who reads it.
 */

export const DatabasePropertyType = z.enum(['TITLE', 'TEXT', 'NUMBER', 'SELECT', 'MULTI_SELECT', 'STATUS', 'DATE', 'PERSON', 'CHECKBOX', 'URL', 'RELATION']);
export type DatabasePropertyType = z.infer<typeof DatabasePropertyType>;
export const DatabaseViewKind = z.enum(['TABLE', 'BOARD', 'LIST', 'CALENDAR']);
export type DatabaseViewKind = z.infer<typeof DatabaseViewKind>;
/** The documents palette (ADR-0042), so option chips look like callouts and text colors. */
export const DatabaseColor = z.enum(['gray', 'red', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink']);
export type DatabaseColor = z.infer<typeof DatabaseColor>;
/** Status options belong to one of three groups, as in Notion. */
export const StatusGroup = z.enum(['TODO', 'IN_PROGRESS', 'DONE']);
export type StatusGroup = z.infer<typeof StatusGroup>;
export const NumberFormat = z.enum(['NUMBER', 'INTEGER', 'PERCENT', 'BRL', 'USD', 'EUR']);
export type NumberFormat = z.infer<typeof NumberFormat>;

/** Short client- or server-made id of a select option, stable across renames. */
export const OptionId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/, 'must be 1-40 letters, digits, _ or -');
const Name = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .refine((v) => v.trim().length > 0, { message: 'must not be blank' });

export const SelectOption = z.object({ id: OptionId, name: Name(100), color: DatabaseColor, group: StatusGroup.optional() }).strict();
export type SelectOption = z.infer<typeof SelectOption>;
/** An option as sent by clients: the server gives an id to options without one. */
export const SelectOptionInput = SelectOption.extend({ id: OptionId.optional(), color: DatabaseColor.optional() }).strict();
export type SelectOptionInput = z.infer<typeof SelectOptionInput>;

export const PropertyConfig = z
  .object({
    /** SELECT, MULTI_SELECT and STATUS. */
    options: z.array(SelectOption).max(100).optional(),
    /** NUMBER. */
    numberFormat: NumberFormat.optional(),
    /** RELATION: the database whose rows it points at, and the property there that points back, if two-way. */
    targetDatabaseId: Uuid.optional(),
    reversePropertyId: Uuid.nullable().optional(),
  })
  .strict();
export type PropertyConfig = z.infer<typeof PropertyConfig>;

export const PropertyConfigInput = PropertyConfig.extend({ options: z.array(SelectOptionInput).max(100).optional() }).strict();
export type PropertyConfigInput = z.infer<typeof PropertyConfigInput>;

/**
 * A cell: text, URL and dates (YYYY-MM-DD) are strings, numbers are numbers, checkboxes booleans, a
 * select or status the option id, and multi-select, people and relations arrays of ids. Null clears it.
 */
export const CellValue = z.union([z.string().max(2000), z.number(), z.boolean(), z.array(z.string().max(100)).max(100)]);
export type CellValue = z.infer<typeof CellValue>;
export const CellPatch = z.record(z.uuid(), CellValue.nullable());
export type CellPatch = z.infer<typeof CellPatch>;

export const DatabaseFilterOp = z.enum(['eq', 'neq', 'contains', 'not_contains', 'gt', 'gte', 'lt', 'lte', 'empty', 'not_empty']);
export type DatabaseFilterOp = z.infer<typeof DatabaseFilterOp>;
/** One condition; a view's filters all apply (AND). `value` is "me" for the reader in person filters. */
export const DatabaseFilter = z
  .object({ propertyId: Uuid, op: DatabaseFilterOp, value: z.union([z.string().max(500), z.number(), z.boolean()]).nullable().optional() })
  .strict();
export type DatabaseFilter = z.infer<typeof DatabaseFilter>;
export const DatabaseSort = z.object({ propertyId: Uuid, direction: SortDirection }).strict();
export type DatabaseSort = z.infer<typeof DatabaseSort>;

export const DatabaseViewConfig = z
  .object({
    /** Properties not shown in this view (new properties show everywhere). */
    hidden: z.array(Uuid).max(200).optional(),
    /** Column widths in pixels, by property id (table views). */
    widths: z.record(z.uuid(), z.number().int().min(60).max(800)).optional(),
    filters: z.array(DatabaseFilter).max(20).optional(),
    sorts: z.array(DatabaseSort).max(5).optional(),
    /** BOARD: a SELECT, STATUS or PERSON property. */
    groupBy: Uuid.nullable().optional(),
    /** CALENDAR: a DATE property. */
    dateProperty: Uuid.nullable().optional(),
  })
  .strict();
export type DatabaseViewConfig = z.infer<typeof DatabaseViewConfig>;

export const DatabasePropertyView = z.object({ id: Uuid, name: z.string(), type: DatabasePropertyType, config: PropertyConfig, position: z.number().int() });
export type DatabasePropertyView = z.infer<typeof DatabasePropertyView>;
export const DatabaseViewView = z.object({ id: Uuid, name: z.string(), kind: DatabaseViewKind, config: DatabaseViewConfig, position: z.number().int() });
export type DatabaseViewView = z.infer<typeof DatabaseViewView>;

export const DocumentDatabaseView = z.object({
  id: Uuid,
  homeDocumentId: Uuid,
  homeDocumentTitle: z.string(),
  title: z.string(),
  icon: z.string().nullable(),
  version: z.number().int(),
  rowCount: z.number().int(),
  /** Whether the reader edits rows, properties and views (the home document's edit rule). */
  canEdit: z.boolean(),
  /** An archived database's home-document editors restore it. */
  canRestore: z.boolean(),
  properties: z.array(DatabasePropertyView),
  views: z.array(DatabaseViewView),
  archivedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type DocumentDatabaseView = z.infer<typeof DocumentDatabaseView>;

export const DocumentDatabaseSummary = z.object({
  id: Uuid,
  title: z.string(),
  icon: z.string().nullable(),
  homeDocumentId: Uuid,
  homeDocumentTitle: z.string(),
  rowCount: z.number().int(),
  archived: z.boolean(),
  updatedAt: IsoDateTime,
});
export type DocumentDatabaseSummary = z.infer<typeof DocumentDatabaseSummary>;

export const DatabaseRowView = z.object({
  id: Uuid,
  databaseId: Uuid,
  title: z.string(),
  values: z.record(z.string(), CellValue),
  position: z.number(),
  pageDocumentId: Uuid.nullable(),
  version: z.number().int(),
  createdBy: UserRef.nullable(),
  updatedBy: UserRef.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: IsoDateTime.nullable(),
});
export type DatabaseRowView = z.infer<typeof DatabaseRowView>;

/** A related row's title for relation chips; rows of databases the reader cannot read are left out. */
export const RelatedRow = z.object({ id: Uuid, databaseId: Uuid, title: z.string() });
export type RelatedRow = z.infer<typeof RelatedRow>;

export const DatabaseRowsPage = z.object({
  rows: z.array(DatabaseRowView),
  /** Rows matching the filters and search, over every page. */
  total: z.number().int(),
  nextCursor: z.string().nullable(),
  /** People named in PERSON cells of these rows. */
  people: z.array(UserRef),
  /** Rows named in RELATION cells of these rows that the reader can read. */
  related: z.array(RelatedRow),
});
export type DatabaseRowsPage = z.infer<typeof DatabaseRowsPage>;

// ---------------------------------------------------------------- Commands

const DatabaseTitle = Name(200);

export const CreateDocumentDatabase = z
  .object({
    homeDocumentId: Uuid,
    title: DatabaseTitle.optional(),
    icon: DocumentIcon.optional(),
    /** The first view: a table (default) or a board grouped by the default Status property. */
    viewKind: z.enum(['TABLE', 'BOARD']).optional(),
  })
  .strict();
export type CreateDocumentDatabase = z.infer<typeof CreateDocumentDatabase>;

export const UpdateDocumentDatabase = z
  .object({
    expectedVersion: Version.optional(),
    title: DatabaseTitle.optional(),
    icon: DocumentIcon.nullable().optional(),
    archived: z.boolean().optional(),
  })
  .strict();
export type UpdateDocumentDatabase = z.infer<typeof UpdateDocumentDatabase>;

export const CreateDatabaseProperty = z
  .object({
    name: Name(100),
    type: DatabasePropertyType.exclude(['TITLE']),
    config: PropertyConfigInput.optional(),
    /** RELATION: also add a property to the target database that points back (needs edit rights there). */
    twoWay: z.boolean().optional(),
    /** Place it at this position (0-based); the end by default. */
    position: z.number().int().min(0).max(1000).optional(),
  })
  .strict();
export type CreateDatabaseProperty = z.infer<typeof CreateDatabaseProperty>;

export const UpdateDatabaseProperty = z
  .object({
    name: Name(100).optional(),
    /** Changing the type converts every value; values that cannot convert need `allowLoss`. */
    type: DatabasePropertyType.exclude(['TITLE']).optional(),
    config: PropertyConfigInput.optional(),
    allowLoss: z.boolean().optional(),
  })
  .strict();
export type UpdateDatabaseProperty = z.infer<typeof UpdateDatabaseProperty>;

export const ReorderDatabaseItems = z.object({ ids: z.array(Uuid).min(1).max(200) }).strict();
export type ReorderDatabaseItems = z.infer<typeof ReorderDatabaseItems>;

export const CreateDatabaseRow = z
  .object({
    values: CellPatch.optional(),
    /** Insert right after this row; at the end by default. */
    afterRowId: Uuid.nullable().optional(),
  })
  .strict();
export type CreateDatabaseRow = z.infer<typeof CreateDatabaseRow>;

/** Cell changes merge: each named cell takes the new value (last write wins per cell), others stay. */
export const UpdateDatabaseRow = z.object({ values: CellPatch.optional(), archived: z.boolean().optional() }).strict();
export type UpdateDatabaseRow = z.infer<typeof UpdateDatabaseRow>;

/** Puts the row between two neighbors of the manual order (either may be null at an end). */
export const MoveDatabaseRow = z.object({ beforeRowId: Uuid.nullable().optional(), afterRowId: Uuid.nullable().optional() }).strict();
export type MoveDatabaseRow = z.infer<typeof MoveDatabaseRow>;

export const CreateDatabaseViewInput = z.object({ name: Name(100), kind: DatabaseViewKind, config: DatabaseViewConfig.optional() }).strict();
export type CreateDatabaseViewInput = z.infer<typeof CreateDatabaseViewInput>;
export const UpdateDatabaseViewInput = z.object({ name: Name(100).optional(), config: DatabaseViewConfig.optional() }).strict();
export type UpdateDatabaseViewInput = z.infer<typeof UpdateDatabaseViewInput>;

const JsonList = (what: string) => z.string().max(8000).describe(`JSON list of ${what}`);

export const DatabaseRowsQuery = z
  .object({
    /** The view whose filters and sorts apply; `filters` and `sorts` replace them for this read only. */
    viewId: Uuid.optional(),
    filters: JsonList('{ propertyId, op, value }').optional(),
    sorts: JsonList('{ propertyId, direction }').optional(),
    /** Matches row titles. */
    q: z.string().max(200).optional(),
    cursor: z.string().max(200).optional(),
    limit: z.string().regex(/^\d{1,3}$/).optional(),
    includeArchived: z.enum(['true', 'false']).optional(),
  })
  .strict();
export type DatabaseRowsQuery = z.infer<typeof DatabaseRowsQuery>;

export const DatabaseListQuery = z.object({ q: z.string().max(200).optional(), homeDocumentId: Uuid.optional(), limit: z.string().regex(/^\d{1,3}$/).optional() }).strict();
export type DatabaseListQuery = z.infer<typeof DatabaseListQuery>;

/**
 * A whole database in one call (`createDatabaseFrom`), for importers such as the Notion API importer:
 * properties are named by a caller key, and row values use those keys.
 */
export const ImportDocumentDatabase = z
  .object({
    homeDocumentId: Uuid,
    title: DatabaseTitle,
    icon: DocumentIcon.optional(),
    properties: z
      .array(z.object({ key: z.string().min(1).max(100), name: Name(100), type: DatabasePropertyType, config: PropertyConfigInput.optional() }).strict())
      .min(1)
      .max(100),
    rows: z.array(z.record(z.string(), CellValue.nullable())).max(5000),
    viewKind: DatabaseViewKind.optional(),
  })
  .strict();
export type ImportDocumentDatabase = z.infer<typeof ImportDocumentDatabase>;

/** What an export shows for a database block; null when the reader cannot read it (never its title). */
export const DatabaseSnapshotView = z.object({
  databaseId: Uuid,
  viewId: Uuid.nullable(),
  snapshot: z.object({ title: z.string(), columns: z.array(z.string()), rows: z.array(z.array(z.string())), more: z.number().int().optional() }).nullable(),
});
export type DatabaseSnapshotView = z.infer<typeof DatabaseSnapshotView>;
export const DatabaseSnapshotsQuery = z
  .object({ blocks: z.string().min(1).max(1600).regex(/^[0-9a-fA-F-]{36}:([0-9a-fA-F-]{36}|-)(,[0-9a-fA-F-]{36}:([0-9a-fA-F-]{36}|-)){0,19}$/, 'must be up to 20 databaseId:viewId pairs (viewId or -)') })
  .strict();
export type DatabaseSnapshotsQuery = z.infer<typeof DatabaseSnapshotsQuery>;
